import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  DataSubjectAutoComplete,
  ExpandableLongText,
  FormField,
  InputDataSubjectDetailsUrl,
  registerTableFilter,
  SampleDataSubjectFormDataType,
  TanstackFormTable,
  TextEllipsis,
  Tooltip,
  SystemClassifierItem,
  PermissionCode,
  formatDateTimeShort,
  useSystemClassifierSelectOptions,
  CreateTableRowButton,
  DeleteTableRowButton,
  emitFocusFormInput,
  useEditSessionFormRowsContext,
  useEditSessionContext,
  type TableProps,
} from '@atlas-frontend/sdk';
import {
  CellContext,
  ColumnDef,
  ColumnHelper,
  createColumnHelper,
  FilterFn,
} from '@tanstack/react-table';
import { Button, Flex, Input } from 'antd';
import {
  FC,
  ForwardedRef,
  ChangeEvent,
  forwardRef,
  memo,
  useMemo,
  useState,
} from 'react';
import { Table } from '@tanstack/react-table';
import * as Icon from 'react-feather';
import { get, useFormContext } from 'react-hook-form';
import translations from './SampleDataSubjectTable.translations.json';
import { SampleUserSelectFilterInput } from './SampleUserSelectFilterInput';
import { SampleLockStateSelectFilterInput } from './SampleLockStateSelectFilterInput';
import { SampleSubjectLockButton } from './sampleSubjectLock/SampleSubjectLockButton';
import { useSampleContext } from './state/SampleContext';
import { expand } from 'uri-template-lite';
import { UiLink } from '../../paths/UiLink';
import { path } from '../../paths';
import { colorTableOperationIcon } from '@atlas-frontend/design-tokens';
import { useSampleDetailsContext } from './state/SampleDetailsContext';
import { useHasPermissionToManageSample } from './useHasPermissionToManageSample';

const { addTranslations, useTranslation } = i18nNsFactory('sample-table');
addTranslations(translations);

const columnHelper =
  createColumnHelper<SampleDataSubjectFormDataType>() as ColumnHelper<SampleDataSubjectFormDataType>;

export type SampleDataSubjectTableProps = (
  | {
      groupId: number;
      descriptionId?: number;
    }
  | {
      groupId?: undefined;
      descriptionId: number;
    }
) & {
  isMutating: boolean;
  isLoading: boolean;
} & Pick<
    TableProps<SampleDataSubjectFormDataType>,
    'onFilter' | 'onSort' | 'filters' | 'sort'
  >;

export const SampleDataSubjectTable = forwardRef(
  (
    props: SampleDataSubjectTableProps,
    ref: ForwardedRef<Table<SampleDataSubjectFormDataType>>,
  ) => {
    const {
      groupId,
      descriptionId,
      isMutating,
      onFilter,
      onSort,
      filters,
      sort,
      isLoading,
    } = props;
    const { t } = useTranslation();
    const { isEditing } = useSampleDetailsContext();
    const { sessionId } = useEditSessionContext();
    const { unvalidatedRowIndex, isPreparingPendingRow, removeRow } =
      useEditSessionFormRowsContext();

    const hasPermissionToManageSample = useHasPermissionToManageSample(
      groupId ?? descriptionId,
      groupId ? 'group' : 'description',
    );
    const hasPermissionToRemoveFromSample = useHasPermissionToManageSample(
      groupId ?? descriptionId,
      groupId ? 'group' : 'description',
      PermissionCode.SAMPLE_SUBJECT_REMOVE,
    );
    const { DATA_LOADING_STATUS: statusOptions } =
      useSystemClassifierSelectOptions(
        ['FREQUENCY', 'DATA_LOADING_STATUS'],
        'id',
      );

    const columns = useMemo(() => {
      const cols: ColumnDef<SampleDataSubjectFormDataType, any>[] = [
        columnHelper.accessor('dataSubject.name', {
          id: 'dataSubjectName',
          header: t('column.dataSubject'),
          cell: (props) => {
            const name = `samples.${props.row.index}.dataSubject`;
            const { dataSubject } = props.row.original;
            return (
              <DataSubjectAutoComplete
                name={name}
                preview={{
                  previewOnly: Boolean(dataSubject.id),
                  render: (value) => (
                    <UiLink
                      id={`navigate-to-dataSubject-${props.row.index}`}
                      path={path.dataSubjectView}
                      params={value}
                    >
                      {value.name}
                    </UiLink>
                  ),
                }}
              />
            );
          },
          meta: {
            width: '40ch',
          },
        }),
        columnHelper.accessor('legalForm', {
          id: 'legalFormName',
          header: t('column.legalForm'),
          cell: (props) => <TextEllipsis>{props.renderValue()}</TextEllipsis>,
          meta: {
            width: '20ch',
          },
          enableColumnFilter: false,
        }),
        columnHelper.accessor('statusId', {
          id: 'statusIds',
          header: t('column.status'),
          cell: StatusCell,
          filterFn: 'includesOption',
          meta: { width: '25ch' },
        }),
        columnHelper.accessor('dataManager.username', {
          id: 'dataManagerUsername',
          header: t('column.manager'),
          meta: {
            width: '15ch',
          },
          filterFn: 'sampleUserSelect',
        }),
        columnHelper.accessor('dataModifiedBy.username', {
          id: 'dataModifiedByUsername',
          header: t('column.dataModifiedBy'),
          cell: DataModifiedByCell,
          meta: { width: '20ch' },
          filterFn: 'sampleUserSelect',
        }),
        columnHelper.accessor('dataModifiedAt', {
          header: t('column.dataModifiedAt'),
          meta: { width: '30ch' },
          cell: DataModifiedAtCell,
          filterFn: 'formattedDate',
          sortingFn: 'formattedDateTime',
        }),
        columnHelper.accessor('comments', {
          header: t('column.comments'),
          cell: CommentCell,
          enableColumnFilter: false,
        }),
      ];

      if (!isMutating) {
        cols.push(
          columnHelper.accessor('isLocked', {
            id: 'lockState',
            header: t('column.isLocked'),
            cell: (props) => (
              <Flex justify="center" align="center">
                <SampleSubjectLockButton
                  formDataIndex={props.row.index}
                  readOnly
                />
              </Flex>
            ),
            meta: {
              width: 100,
            },
            filterFn: 'sampleLockStateSelect',
            sortingFn: (rowA, rowB) => {
              const getValue = (row: SampleDataSubjectFormDataType) =>
                row.isLocked
                  ? 2
                  : row.sampleDataObjects.some(({ isLocked }) => isLocked)
                    ? 1
                    : 0;
              return getValue(rowA.original) - getValue(rowB.original);
            },
          }),
          columnHelper.display({
            id: 'openDataSubject',
            header: '',
            cell: (props) => (
              <Flex justify="center" align="center">
                <Tooltip title={t('button.openDataSubject')} placement="left">
                  <Button
                    id={`open-dataSubject-${props.row.index}`}
                    type="text"
                    shape="circle"
                    size="small"
                    icon={
                      <Icon.FileText
                        size={18}
                        color={colorTableOperationIcon}
                      />
                    }
                    href={expand(InputDataSubjectDetailsUrl, {
                      descriptionId,
                      groupId,
                      date: props.row.original.valueDate,
                      subject: props.row.original.dataSubject.code,
                    })}
                  />
                </Tooltip>
              </Flex>
            ),
            meta: {
              className: 'sticky-right',
              width: 50,
            },
            enableColumnFilter: false,
            enableSorting: false,
          }),
        );
      }

      if (isMutating && hasPermissionToManageSample) {
        cols.push(
          columnHelper.display({
            id: 'operations',
            header: () =>
              isEditing && (
                <Flex justify="center" align="center">
                  <AddSampleRowButton
                    disabled={sessionId == null || unvalidatedRowIndex !== null}
                  />
                </Flex>
              ),
            enableSorting: false,
            enableColumnFilter: false,
            cell: (props) => {
              return (
                <Flex justify="center" align="center" gap="small">
                  <SampleSubjectLockButton formDataIndex={props.row.index} />
                  {hasPermissionToRemoveFromSample && isEditing && (
                    <DeleteTableRowButton
                      id={`delete-table-row-${props.row.index}`}
                      confirm={{
                        onConfirm: () => {
                          removeRow(props.row.original, props.row.index);
                        },
                      }}
                    />
                  )}
                </Flex>
              );
            },
            meta: {
              width: 88,
              className: 'sticky-right',
            },
            size: 0,
          }),
        );
      }
      return cols;
    }, [
      t,
      isMutating,
      hasPermissionToManageSample,
      descriptionId,
      groupId,
      isEditing,
      sessionId,
      unvalidatedRowIndex,
      hasPermissionToRemoveFromSample,
      removeRow,
    ]);

    const options = useMemo(
      () => ({
        statusIds: statusOptions.map((status) => ({
          id: status.value,
          name: status.label,
        })),
        isLocked: [
          { id: true, name: t('yes') },
          { id: false, name: t('no') },
        ],
      }),
      [statusOptions, t],
    );

    return (
      <TanstackFormTable
        ref={ref}
        columns={columns}
        options={options}
        name="samples"
        sticky
        scrollContainerSelector="#detail-layout-main-content"
        manualFiltering
        manualSorting
        onFilter={onFilter}
        onSort={onSort}
        filters={filters}
        sort={sort}
        disableHeader={unvalidatedRowIndex !== null}
        isLoading={isLoading || isPreparingPendingRow}
      />
    );
  },
);

declare module '@tanstack/react-table' {
  interface FilterFns {
    sampleLockStateSelect: FilterFn<any>;
    sampleUserSelect: FilterFn<any>;
  }
}
registerTableFilter<SampleDataSubjectFormDataType>(
  'sampleLockStateSelect',
  () => true, // server side filtering
  SampleLockStateSelectFilterInput,
);
registerTableFilter<SampleDataSubjectFormDataType>(
  'sampleUserSelect',
  () => true, // server side filtering
  SampleUserSelectFilterInput,
);

// New samples are listed on the last page, so add the row there.
const AddSampleRowButton = ({ disabled }: { disabled: boolean }) => {
  const { addRow, goToLastPage } = useEditSessionFormRowsContext();
  const { getValues } = useFormContext();

  const handleClick = async () => {
    await goToLastPage();
    addRow();
    const index = getValues('samples').length - 1;
    setTimeout(() => emitFocusFormInput(`samples.${index}.dataSubject`));
  };

  return <CreateTableRowButton onClick={handleClick} disabled={disabled} />;
};

const StatusCell: FC<CellContext<SampleDataSubjectFormDataType, any>> = memo(
  (props) => {
    const { selectedGroupMemberId } = useSampleContext();
    const selected = selectedGroupMemberId ? selectedGroupMemberId : undefined;
    const sampleDataObjectIndex = getSampleDataObjectIndex(
      props.row.original,
      selected,
    );

    const itemId = selected
      ? props.row.original.sampleDataObjects[sampleDataObjectIndex]?.statusId
      : props.row.original.statusId;

    if (!itemId) return null;
    return <SystemClassifierItem code="DATA_LOADING_STATUS" itemId={itemId} />;
  },
);

const DataModifiedByCell: FC<CellContext<SampleDataSubjectFormDataType, any>> =
  memo((props) => {
    const { sampleDataObjects, dataModifiedBy: groupDataModifiedBy } =
      props.row.original;
    const { selectedGroupMemberId } = useSampleContext();
    const selected = selectedGroupMemberId ? selectedGroupMemberId : undefined;
    const sampleDataObjectIndex = getSampleDataObjectIndex(
      props.row.original,
      selected,
    );

    const dataModifiedBy = selected
      ? sampleDataObjects[sampleDataObjectIndex]?.dataModifiedBy
      : groupDataModifiedBy;

    if (!dataModifiedBy) return null;
    return <TextEllipsis>{dataModifiedBy.username}</TextEllipsis>;
  });

const DataModifiedAtCell: FC<CellContext<SampleDataSubjectFormDataType, any>> =
  memo((props) => {
    const { sampleDataObjects, dataModifiedAt: groupDataModifiedAt } =
      props.row.original;
    const { selectedGroupMemberId } = useSampleContext();
    const selected = selectedGroupMemberId ? selectedGroupMemberId : undefined;
    const sampleDataObjectIndex = getSampleDataObjectIndex(
      props.row.original,
      selected,
    );

    const dataModifiedAt = selected
      ? sampleDataObjects[sampleDataObjectIndex]?.dataModifiedAt
      : groupDataModifiedAt;

    if (!dataModifiedAt) return null;
    return <TextEllipsis>{formatDateTimeShort(dataModifiedAt)}</TextEllipsis>;
  });

const CommentCell: FC<CellContext<SampleDataSubjectFormDataType, any>> = memo(
  (props) => {
    const { isEditing } = useSampleDetailsContext();
    const { selectedGroupMemberId } = useSampleContext();
    const fieldName = getCommentsFieldName(
      props.row.original,
      selectedGroupMemberId,
    );
    const name = `samples.${props.row.index}.${fieldName}`;

    if (isEditing) {
      return (
        <FormField
          key={name}
          name={name}
          size="small"
          preview={{
            ellipsis: false,
            render: (value) => (
              <ExpandableLongText hideExpandButton>{value}</ExpandableLongText>
            ),
          }}
        >
          <CommentDraftInput name={name} />
        </FormField>
      );
    }

    return (
      <ExpandableLongText id={`sampleDataSubject-comment-${props.row.index}`}>
        {get(props.row.original, fieldName)}
      </ExpandableLongText>
    );
  },
);

const CommentDraftInput: FC<{
  name: string;
}> = ({ name }) => {
  const { setValue } = useFormContext();
  const [value, setDraftValue] = useState('');

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    setDraftValue(nextValue);
    setValue(name, nextValue, { shouldDirty: true });
  };

  return (
    <Input.TextArea
      autoFocus
      id={name}
      maxLength={2000}
      rows={3}
      value={value}
      onChange={handleChange}
    />
  );
};

const getCommentsFieldName = (
  rowData: SampleDataSubjectFormDataType,
  selectedGroupMemberId?: number,
) =>
  selectedGroupMemberId
    ? `sampleDataObjects.${getSampleDataObjectIndex(
        rowData,
        selectedGroupMemberId,
      )}.comments`
    : `comments`;

const getSampleDataObjectIndex = (
  tableRow: SampleDataSubjectFormDataType,
  selected?: number,
) =>
  selected
    ? tableRow.sampleDataObjects.findIndex(
        ({ dataObjectDescriptionId }) => dataObjectDescriptionId === selected,
      )
    : -1;
